import { useState, useRef, useEffect } from 'react';
import { PopUpAlert } from '../../../../Commons/Helpers';
import { useContent } from '../../../../Content/cms';

export function useVolumeLevel(mic: MediaStream | null = null) {
    const [volumeLevel, setVolumeLevel] = useState(0);
    const getContent = useContent();
    const isMounted = useRef<boolean>(true);

    const micStream = useRef<MediaStream | null>(mic);
    const destroyMediaStream = useRef<boolean>(false);
    const audioContext = useRef<AudioContext | null>(null);
    const analyser = useRef<AnalyserNode | null>(null);
    const workletNode = useRef<AudioWorkletNode | null>(null);

    async function stopVolumeLevel() {
        setVolumeLevel(0);
        workletNode.current?.disconnect();
        workletNode.current = null;
        analyser.current?.disconnect();
        analyser.current = null;
        if (audioContext.current) {
            await audioContext.current.close();
            audioContext.current = null;
        }
        if (destroyMediaStream.current) {
            micStream.current?.getTracks().forEach(track => track.stop());
            micStream.current = null;
        }
    }

    function _captureMicrophone(deviceID: string, callback: (mic: MediaStream) => void) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            PopUpAlert(getContent('protocol__mic_permission__no_volumne_error'));
            return;
        }
        navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: { exact: deviceID },
                echoCancellation: false
            },
            video: false
        }).then((mic: MediaStream) => {
            micStream.current = mic;
            destroyMediaStream.current = true;
            callback(mic);
        }).catch(function (error) {
            alert('[UseVolume] Unable to capture your microphone. Please check console logs.');
            console.error(error);
        });
    }

    async function _startPollingVolume(callback: (volume: number) => void) {
        if (micStream.current === null) {
            console.error('[UseVolume] No microphone stream available');
            return;
        }
        audioContext.current = new AudioContext();
        analyser.current = audioContext.current.createAnalyser();
        analyser.current.smoothingTimeConstant = 0.8;
        analyser.current.fftSize = 1024;
        audioContext.current.createMediaStreamSource(micStream.current).connect(analyser.current);

        if (!audioContext.current.audioWorklet) {
            console.error('[UseVolume] AudioWorklet not supported');
            return;
        }
        
        // Add the AudioWorklet module
        await audioContext.current.audioWorklet.addModule('/volume-processor.js');
        
        // Create an AudioWorkletNode using the registered processor
        workletNode.current = new AudioWorkletNode(audioContext.current, 'volume-processor');
        audioContext.current.createMediaStreamSource(micStream.current).connect(workletNode.current);
        workletNode.current.port.onmessage = (event) => {
            // Only update state if component is still mounted
            if (isMounted.current) {
                const avg = event.data;
                const scaled = Math.min(100, Math.max(0, avg * 100));
                setVolumeLevel(scaled);
                callback(scaled);
            }
        };
    }

    function startVolumeLevel(callback: (volume: number) => void = () => {}, deviceID?: string) {
        if (micStream.current === null && deviceID !== undefined) {
            _captureMicrophone(deviceID, () => _startPollingVolume(callback));
        } else if (micStream.current !== null) {
            _startPollingVolume(callback);
        } else {
            console.error('[UseVolume] No microphone stream available');
        }
    }

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            isMounted.current = false;
            stopVolumeLevel();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return { startVolumeLevel, stopVolumeLevel, volumeLevel };
}